import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../../server/firebase';
import './asesores.css';

function Asesores({ userEmail }) {
  const [interesados, setInteresados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [asesorName, setAsesorName] = useState('');
  const [observationModal, setObservationModal] = useState(null);
  const [observationDescription, setObservationDescription] = useState('');
  const [observationFechaProxima, setObservationFechaProxima] = useState('');
  const [savingObservation, setSavingObservation] = useState(false);

  useEffect(() => {
    const fetchInteresados = async () => {
      try {
        // Traer todos los interesados
        let querySnapshot = await getDocs(collection(db, 'INTERESADOS'));
        if (querySnapshot.empty) {
          querySnapshot = await getDocs(collection(db, 'interesados'));
        }

        // Procesar datos y filtrar los asignados a este asesor
        const allInteresados = [];
        querySnapshot.docs.forEach(doc => {
          const docData = doc.data();
          
          // Iterar sobre las entradas del documento
          Object.entries(docData).forEach(([key, value]) => {
            if (typeof value === 'object' && value !== null && value.asesorasignado === userEmail) {
              const observaciones = Object.entries(value)
                .filter(([subKey, subValue]) => /^observacion\d+$/i.test(subKey) && typeof subValue === 'object' && subValue !== null)
                .sort((a, b) => {
                  const aNum = parseInt(a[0].replace(/\D/g, ''), 10) || 0;
                  const bNum = parseInt(b[0].replace(/\D/g, ''), 10) || 0;
                  return aNum - bNum;
                })
                .map(([subKey, subValue]) => ({
                  key: subKey,
                  description: subValue.description || '',
                  fechaDeObservacion: subValue.fechaDeObservacion || '',
                  fechaProxima: subValue.fechaProxima || ''
                }));

              allInteresados.push({
                id: doc.id,
                userKey: key,
                nombre: value.nombre || 'Sin nombre',
                telefono: value.telefono || 'Sin teléfono',
                correo: value.correo || 'Sin correo',
                asesorasignado: value.asesorasignado,
                fechaAsignacion: value.fechaAsignacion,
                descuento: value.descuento || null,
                observaciones
              });
            }
          });
        });

        setInteresados(allInteresados);
      } catch (error) {
        console.error('Error fetching interesados:', error);
        setError('Error al obtener interesados.');
      } finally {
        setLoading(false);
      }
    };

    const fetchAsesorName = async () => {
      try {
        const docRef = doc(db, 'ADMINISTRADORES', 'ASESOR');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const adminData = docSnap.data();
          if (adminData && adminData[userEmail] && adminData[userEmail].name) {
            setAsesorName(adminData[userEmail].name);
          }
        }
      } catch (err) {
        console.error('Error fetching asesor name:', err);
      }
    };

    fetchInteresados();
    fetchAsesorName();
  }, [userEmail]);

  const openObservationModal = (interesado) => {
    setObservationModal(interesado);
    setObservationDescription('');
    setObservationFechaProxima('');
  };

  const closeObservationModal = () => {
    setObservationModal(null);
    setObservationDescription('');
    setObservationFechaProxima('');
  };

  const handleSaveObservation = async () => {
    if (!observationModal) return;
    const description = observationDescription.trim();
    const fechaProxima = observationFechaProxima;

    if (!description) {
      alert('Debes escribir la observación.');
      return;
    }

    if (!fechaProxima) {
      alert('Debes seleccionar la fecha de próxima observación.');
      return;
    }

    setSavingObservation(true);

    try {
      const interesado = observationModal;
      const docRef = doc(db, 'INTERESADOS', interesado.id);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        alert('No se encontró el interesado en la base de datos.');
        setSavingObservation(false);
        return;
      }

      const currentData = docSnap.data();
      const currentUserData = currentData[interesado.userKey] || {};
      const observacionKeys = Object.keys(currentUserData)
        .filter(key => /^observacion\d+$/i.test(key));
      const nextNumber = observacionKeys.reduce((max, key) => {
        const num = parseInt(key.replace(/\D/g, ''), 10);
        return Number.isNaN(num) ? max : Math.max(max, num);
      }, 0) + 1;
      const nextKey = `observacion${nextNumber}`;
      const newObservation = {
        description,
        fechaDeObservacion: new Date().toISOString(),
        fechaProxima
      };

      await updateDoc(docRef, {
        [`${interesado.userKey}.${nextKey}`]: newObservation
      });

      setInteresados(prev => prev.map(item => {
        if (item.id !== interesado.id || item.userKey !== interesado.userKey) return item;
        return {
          ...item,
          observaciones: [...item.observaciones, { key: nextKey, ...newObservation }]
        };
      }));

      closeObservationModal();
    } catch (err) {
      console.error('Error guardando observación:', err);
      alert('Error al guardar la observación. Intenta nuevamente.');
    } finally {
      setSavingObservation(false);
    }
  };

  if (loading) {
    return <div className="asesores-container"><p>Cargando...</p></div>;
  }

  if (error) {
    return <div className="asesores-container"><p className="error">{error}</p></div>;
  }

  return (
    <div className="asesores-container">
      <h1>Mis Interesados{asesorName ? ` - ${asesorName}` : ''}</h1>
      {interesados.length === 0 ? (
        <p className="no-data">No tienes interesados asignados</p>
      ) : (
        <table className="asesores-table">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Teléfono</th>
              <th>Correo</th>
              <th>Fecha de Asignación</th>
            </tr>
          </thead>
          <tbody>
            {interesados.map((interesado, idx) => {

              return (
                <React.Fragment key={idx}>
                  <tr>
                    <td>{interesado.nombre}</td>
                    <td>
                      {interesado.telefono && interesado.telefono !== 'Sin teléfono' ? (
                        <a href={`https://wa.me/57${interesado.telefono}`} target="_blank" rel="noreferrer">+57 {interesado.telefono}</a>
                      ) : (
                        'Sin teléfono'
                      )}
                    </td>
                    <td>{interesado.correo}</td>
                    <td>{interesado.fechaAsignacion ? new Date(interesado.fechaAsignacion).toLocaleDateString('es-CO') : 'No asignado'}</td>
                  </tr>
                  <tr>
                    <td colSpan="4" className="observaciones-cell">
                      <div className="observaciones-list">
                        <strong>Observaciones:</strong>
                        {interesado.observaciones.length === 0 ? (
                          <p className="no-data">No hay observaciones todavía.</p>
                        ) : (
                          <ul>
                            {interesado.observaciones.map(obs => (
                              <li key={obs.key}>
                                <strong>{obs.key}:</strong> {obs.description} <br />
                                <small>Creada: {obs.fechaDeObservacion ? new Date(obs.fechaDeObservacion).toLocaleDateString('es-CO') : '---'}</small> ·
                                <small> Próxima: {obs.fechaProxima ? new Date(obs.fechaProxima).toLocaleDateString('es-CO') : '---'}</small>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div className="observacion-actions">
                        <button
                          type="button"
                          className="add-observation-btn"
                          onClick={() => openObservationModal(interesado)}
                        >
                          Agregar Observación
                        </button>
                      </div>
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      {observationModal && (
        <div className="overlay-observation">
          <div className="modal-observation">
            <div className="modal-header">
              <h2>Agregar Observación para {observationModal.nombre}</h2>
              <button className="close-modal-btn" onClick={closeObservationModal}>×</button>
            </div>
            <div className="modal-body">
              <label>
                Observación:
                <textarea
                  value={observationDescription}
                  onChange={(e) => setObservationDescription(e.target.value)}
                  rows={5}
                  placeholder="Escribe la observación aquí..."
                />
              </label>
              <label>
                Fecha próxima:
                <input
                  type="date"
                  value={observationFechaProxima}
                  onChange={(e) => setObservationFechaProxima(e.target.value)}
                />
              </label>
            </div>
            <div className="modal-footer">
              <button className="secondary-btn" onClick={closeObservationModal}>Cancelar</button>
              <button className="primary-btn" onClick={handleSaveObservation} disabled={savingObservation}>
                {savingObservation ? 'Guardando...' : 'Guardar observación'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Asesores;
